import { refreshMe, selectMe, updateMe } from 'domains/account';
import { useAppDispatch, useAppSelector } from 'lib/redux';
import { PermissionLevels } from 'lib/types';
import { updateTeam } from 'domains/team';
import { baseRESTUrl } from 'lib/utils';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { getSubscription } from 'domains/subscription';
import { AlertBanner } from './alertBanner';

export const DeleteWarningBanner = () => {
  const me = useAppSelector(selectMe);
  const dispatch = useAppDispatch();
  const [processing, setProcessing] = useState<boolean>(false);

  useEffect(() => {
    const body = document.getElementsByTagName('body')[0];

    if (!me.team.delete_scheduled && !me.team.owner_delete_scheduled) {
      body.classList.remove('banner-bump');
    }
    else {
      body.classList.add('banner-bump');
    }
  }, [me]);

  const undoTeamDelete = async () => {
    setProcessing(true);
    try {
      await dispatch(updateTeam({delete_scheduled: false})).unwrap();
      await dispatch(refreshMe(baseRESTUrl())).unwrap();
      await dispatch(getSubscription()).unwrap();
    }
    catch(e) {
      console.error(e);
    }
    finally {
      setProcessing(false);
    }
  }

  const undoUserDelete = async () => {
    setProcessing(true);
    try{
      await dispatch(updateMe({delete_scheduled: false})).unwrap();
      await dispatch(refreshMe(baseRESTUrl())).unwrap();
    }
    catch(e) {
      console.error(e);
    }
    finally {
      setProcessing(false);
    }
  }

  if (!me.team.delete_scheduled && !me.team.owner_delete_scheduled && !me.user.delete_scheduled) {
    return null;
  }

  const canUndo = (me.team.delete_scheduled && (me.permissions === PermissionLevels.ADMIN || me.permissions === PermissionLevels.OWNER)) || (me.team.owner_delete_scheduled && me.permissions === PermissionLevels.OWNER) || me.user.delete_scheduled;

  if (!canUndo) {
    return (
      <AlertBanner
        title={`This team has been scheduled to be deleted on ${dayjs(me.team.owner_delete_on || me.team.delete_on).format('MMMM D, YYYY')}, after which you will no longer have access to Packs.`}
        description="In accordance with privacy regulations, we cannot reinstate a deleted team. Once it&apos;s gone, it&apos;s gone. Please contact your administrator for details."
      />
    );
  }

  if (me.user.delete_scheduled) {
    return (
      <AlertBanner
        title={`This account will be deleted on ${dayjs(me.user.delete_on).format('MMMM D, YYYY')} and you an any team you created will no longer have access to Packs.`}
        description="In accordance with privacy regulations, we cannot reinstate a deleted team. Once it&apos;s gone, it&apos;s gone. If you don&apos;t want your account to be deleted, please select undo to continue using Packs."
        onClick={undoUserDelete} 
        isProcessing={processing}
      />
    );
  }

  if (me.team.owner_delete_scheduled) {
    return (
      <AlertBanner
        title={`This account will be deleted on ${dayjs(me.team.owner_delete_on).format('MMMM D, YYYY')} and you and your team will no longer have access to Packs.`}
        description="In accordance with privacy regulations, we cannot reinstate a deleted team. Once it&apos;s gone, it&apos;s gone. If you don&apos;t want your account to be deleted, please select undo to continue using Packs."
        onClick={undoUserDelete} 
        isProcessing={processing} 
      />
    );
  }

  if (me.team.delete_scheduled) {
    return (
      <AlertBanner
        title={`This team will be deleted on ${dayjs(me.team.delete_on).format('MMMM D, YYYY')} and you and your team members will no longer have access to Packs.`}
        description="In accordance with privacy regulations, we cannot reinstate a deleted team. Once it&apos;s gone, it&apos;s gone. If you don&apos;t want this team to be deleted, please select undo to continue using Packs."
        onClick={undoTeamDelete} 
        isProcessing={processing}
      />
    );
  }

  return null;   
}